export type CatalogPrefetchEnvironment = {
  visible: boolean;
  online: boolean;
  saveData: boolean;
  effectiveType: string;
};

const CONSTRAINED_CONNECTIONS = new Set(["slow-2g", "2g", "3g"]);

/**
 * Catalog speculation is allowed only after a shopper-intent signal (the
 * caller owns that signal) and only when it will not consume a constrained
 * connection in the background.
 */
export const canPrefetchCatalogForIntent = (
  environment: CatalogPrefetchEnvironment
): boolean =>
  environment.visible &&
  environment.online &&
  !environment.saveData &&
  !CONSTRAINED_CONNECTIONS.has(environment.effectiveType.toLowerCase());

export type CatalogPageIntent = "next" | "previous";

/**
 * The page that a View Next/Previous intent signal (the pointer entering the
 * link, keyboard focus on it, a touch starting on it) may prefetch, or null
 * when there is no such page or a page load is already running. The network
 * gate above still applies.
 */
export const catalogPageIntentTarget = (
  intent: CatalogPageIntent,
  state: { currentPage: number; totalPages: number; busy: boolean }
): number | null => {
  if (state.busy) return null;
  const currentPage = Math.max(1, Math.floor(Number(state.currentPage) || 1));
  const target = intent === "next" ? currentPage + 1 : currentPage - 1;
  const totalPages = Math.floor(Number(state.totalPages) || 0);
  if (target < 1 || target > totalPages) return null;
  return target;
};

export const readCatalogPrefetchEnvironment =
  (): CatalogPrefetchEnvironment => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return {
        visible: false,
        online: false,
        saveData: false,
        effectiveType: "",
      };
    }

    const connection = (navigator as any).connection;
    return {
      visible: document.visibilityState === "visible",
      online: navigator.onLine !== false,
      saveData: connection?.saveData === true,
      effectiveType: String(connection?.effectiveType || ""),
    };
  };
